// pptExtractor.js
// PowerPoint text extraction for FlowTutor using pizzip
// Supports .pptx files (PowerPoint 2007+)
// Usage: import { extractPptText } from './pptExtractor';

import PizZip from 'pizzip';

/**
 * Extracts text content from a PowerPoint (.pptx) file
 * @param {File} file - The PowerPoint file from input[type="file"]
 * @returns {Promise<string>} - The extracted text content
 */
export async function extractPptText(file) {
  try {
    console.log('PowerPoint extraction started for file:', file.name, 'size:', file.size);

    // Validate file
    if (!file) throw new Error('No file provided');
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.pptx') && !fileName.endsWith('.ppt')) {
      throw new Error('Not a PowerPoint file');
    }

    // .ppt (older format) is not supported, only .pptx
    if (fileName.endsWith('.ppt') && !fileName.endsWith('.pptx')) {
      throw new Error('Old PowerPoint format (.ppt) is not supported. Please save your file as .pptx (PowerPoint 2007 or later) and try again.');
    }

    console.log('Reading file as array buffer...');
    const arrayBuffer = await file.arrayBuffer();
    console.log('ArrayBuffer size:', arrayBuffer.byteLength);

    // Load the .pptx file (which is a ZIP archive)
    console.log('Parsing PowerPoint structure...');
    const zip = new PizZip(arrayBuffer);

    // Get all files in the archive
    const files = Object.keys(zip.files);
    console.log('Found files in PowerPoint:', files.length);

    let extractedText = '';
    let slideCount = 0;

    // .pptx files store slides in ppt/slides/slideN.xml
    // We need to extract text from these XML files
    const slideFiles = files.filter(name =>
      name.startsWith('ppt/slides/slide') &&
      name.endsWith('.xml') &&
      !name.includes('_rels')
    );

    console.log('Found slides:', slideFiles.length);

    for (const slideFile of slideFiles) {
      slideCount++;
      try {
        console.log(`Processing slide ${slideCount}: ${slideFile}`);

        // Extract the XML content
        const xmlContent = zip.files[slideFile].asText();

        // Extract text from XML tags
        // PowerPoint stores text in <a:t> tags (within <a:r> runs)
        const textMatches = xmlContent.match(/<a:t[^>]*>(.*?)<\/a:t>/g);

        if (textMatches && textMatches.length > 0) {
          const slideText = textMatches
            .map(match => {
              // Extract text between tags and decode XML entities
              const text = match.replace(/<a:t[^>]*>/, '').replace(/<\/a:t>/, '');
              return decodeXmlEntities(text);
            })
            .join(' ');

          extractedText += `\n--- Slide ${slideCount} ---\n${slideText}\n`;
          console.log(`Slide ${slideCount} text length:`, slideText.length);
        } else {
          console.log(`Slide ${slideCount} has no text content`);
          extractedText += `\n--- Slide ${slideCount} ---\n[No text content]\n`;
        }
      } catch (slideError) {
        console.error(`Error processing slide ${slideCount}:`, slideError);
        extractedText += `\n--- Slide ${slideCount} ---\n[Error extracting slide]\n`;
      }
    }

    // Also extract notes if available (ppt/notesSlides/notesSlideN.xml)
    const notesFiles = files.filter(name =>
      name.startsWith('ppt/notesSlides/notesSlide') &&
      name.endsWith('.xml')
    );

    if (notesFiles.length > 0) {
      console.log('Found speaker notes:', notesFiles.length);
      extractedText += '\n\n=== SPEAKER NOTES ===\n';

      for (let i = 0; i < notesFiles.length; i++) {
        try {
          const xmlContent = zip.files[notesFiles[i]].asText();
          const textMatches = xmlContent.match(/<a:t[^>]*>(.*?)<\/a:t>/g);

          if (textMatches && textMatches.length > 0) {
            const notesText = textMatches
              .map(match => {
                const text = match.replace(/<a:t[^>]*>/, '').replace(/<\/a:t>/, '');
                return decodeXmlEntities(text);
              })
              .join(' ');

            extractedText += `\nNotes for Slide ${i + 1}:\n${notesText}\n`;
          }
        } catch (notesError) {
          console.error(`Error extracting notes for slide ${i + 1}:`, notesError);
        }
      }
    }

    const finalText = extractedText.trim();
    console.log('PowerPoint extraction completed.');
    console.log('Total slides:', slideCount);
    console.log('Total text length:', finalText.length);
    console.log('Extracted text preview:', finalText.substring(0, 300) + '...');

    if (finalText.length === 0) {
      throw new Error('No text content found in PowerPoint file');
    }

    return finalText;
  } catch (error) {
    console.error('PowerPoint extraction error:', error);
    throw new Error(`PowerPoint extraction failed: ${error.message}`);
  }
}

/**
 * Decode XML entities like &amp; &lt; &gt; &quot; &apos;
 */
function decodeXmlEntities(text) {
  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&nbsp;': ' '
  };

  return text.replace(/&[a-z]+;|&#\d+;/gi, match => entities[match] || match);
}
